import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { PageInfo } from 'src/app/core/models/page-info';
import { NotificationService } from 'src/app/core/services/notification-data.service';
import { genTableTranslation, isNullOrEmpty, laaSFormatString } from 'src/app/core/utils/common-functions';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { endOfDay, endOfDecade, startOfDay, subMonths } from 'date-fns';

@Component({
  selector: 'laas-bell-notifications',
  templateUrl: './bell-notifications.component.html',
  styleUrls: ['./bell-notifications.component.scss']
})
export class BellNotificationsComponent extends SessionBaseComponent implements OnInit {

  isSearchFormOpen: boolean = true;
  searchForm: FormGroup;
  bellNotificationData: any[] = [];
  bellNotificationColumns: any[] = [];
  isLoading: boolean = false;
  selectedShowType: any = 'all';
  defaultNotifyDt: Date[] = [startOfDay(subMonths(new Date(), 3)), endOfDay((new Date()))];

  isReadStatusOptions: any[] = [];

  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
  };

  sortOrder: SortOrder = {
    property: "NotifyInformation.NotifyDt",
    direction: -1
  };

  preferenceKey: string = UserPreferenceConstants.BellNotificationsTableData;

  @ViewChild('dataTable') dataTable!: DataTableComponent;

  constructor(
    injector: Injector,
    private formBuilder: FormBuilder,
    private notificationService: NotificationService,
  ) {
    super(injector);

    this.searchForm = this.formBuilder.group({
      textList: null,
      notifyDtStart: this.defaultNotifyDt[0],
      notifyDtEnd: this.defaultNotifyDt[1]
    });

    this.translateService.get('BellNotifications.ReadStatus.Unread').subscribe((translated: string) => {
      this.isReadStatusOptions = [
        { label: this.translateService.instant('BellNotifications.ReadStatus.Unread'), value: 0 },
        { label: this.translateService.instant('BellNotifications.ReadStatus.Read'), value: 1 }
      ];
    });

    this.bellNotificationColumns = this.bellNotificationTableTranslation();
  }

  bellNotificationTableTranslation(): any[] {
    var fields: string[];
    var dates: string[];
    var fieldSorts: string[];
    var jsons: string[];

    fields = ['title', 'notifyDt', 'isRead'];
    dates = ['notifyDt'];
    fieldSorts = [];
    jsons = [
      'BellNotifications.Table.Columns.Title',
      'BellNotifications.Table.Columns.NotifyDt',
      'BellNotifications.Table.Columns.IsReadStatus',
    ];

    return genTableTranslation(this.translateService, fields, dates, fieldSorts, jsons);
  }

  chipsAdd(event: any, fromControlName: any): void {
    const value: string = event.value;
    const parsedValues: string[] = value.split('\r\n').filter(x => isNullOrEmpty(x) === false && x !== '\r\n');
    const formControl = this.searchForm.get(fromControlName);
    formControl?.value.splice(formControl?.value.indexOf(value));
    formControl?.setValue(formControl?.value.concat(parsedValues));
  }

  dateOnChange(date: any, type: string) {
    switch (type) {
      case 'notifyDt':
        this.searchForm.controls.notifyDtStart.setValue(date.startDate);
        this.searchForm.controls.notifyDtEnd.setValue(date.endDate);
        break;
    }
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.searchBellNotifications();
  }

  searchBtnClick(): void {
    this.searchBellNotifications();
  }

  resetBtnClick(): void {
    this.searchForm.reset();
  }

  markRead(markType: string): void {
    let seqNos = null;
    if (markType === 'SelectedRow') {
      seqNos = this.dataTable.selectedRows.map((rowData: any) => { return rowData.seqNo });
    }

    const req = {
      markType: markType,
      seqNos: seqNos,
      uuid: this.userProfile.uuid,
    };

    this.notificationService.markRead(req).subscribe({
      next: () => {
        this.dataTable.selectedRows = [];
        this.dataTable.checkedRows = [];
        this.dataTable.headerCheckBox.writeValue(false);

        this.notificationService.setUpdateBellNotifyFlag(true);
        this.searchBellNotifications();
        this.toastService.success(this.translateService.instant('Common.Toast.SaveSuccess'));
      },
      error: async response => {
        var instantMsg = await this.translateService.get("Common.Toast.SaveFail").toPromise();
        var msg = `${laaSFormatString(instantMsg, [response.status, response.error.errorMsg])}\n`;
        this.toastService.error(msg);
      }
    });
  }

  tablePageChange(event: any): void {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.searchBellNotifications();
  }

  tableSort(event: any): void {
    this.sortOrder = { property: event.field, direction: event.order };
    this.dataTable.setCurrentPage(0);
    this.searchBellNotifications();
  }

  searchBellNotifications(): void {
    this.isLoading = true;
    this.dataTable.setCurrentPage(0);

    const req = {
      notifyType: 'Bell',
      uuid: this.userProfile.uuid,
      isRead: this.selectedShowType === 'all' ? null : 0,
      textList: this.searchForm.get('textList')?.value ? this.searchForm.get('textList')?.value : null,
      notifyDtStart: this.searchForm.get('notifyDtStart')?.value ? this.searchForm.get('notifyDtStart')?.value : null,
      notifyDtEnd: this.searchForm.get('notifyDtEnd')?.value ? this.searchForm.get('notifyDtEnd')?.value : null,
      pageIndex: this.pageInfo.pageIndex,
      pageSize: this.pageInfo.pageSize,
      sortOrder: this.sortOrder
    };

    console.log('search req',req)
    this.notificationService.getNotifyInformation(req).subscribe((response: any) => {
      this.isLoading = false;
      if (!response) {
        return;
      }

      this.bellNotificationData = response.content;
      this.pageInfo.totalRecords = response.totalElements;
      this.dataTable.setPageInfo(this.pageInfo.totalRecords);
    });
  }

  changeBellNotificationData(type: string): void {
    this.searchBellNotifications();
  }

  onRowClick(notification: any): void {
    this.routeStateService.navigateTo(
      '/Notification/BellNotificationsContent',
      {
        uuid: this.userProfile.uuid,
        seqNo: notification.seqNo,
        isRead: notification.isRead,
        title: notification.title,
        content: notification.content,
      },
      false
    );
  }

  async exportBtnClick(event: any): Promise<void> {

    const params = {
      notifyType: 'Bell',
      uuid: this.userProfile.uuid,
      isRead: this.selectedShowType === 'all' ? null : 0,
      textList: this.searchForm.get('textList')?.value ?? this.searchForm.get('textList')?.value,
      notifyDtStart: this.searchForm.get('notifyDtStart')?.value ?? startOfDay(subMonths(new Date(), 3)),
      notifyDtEnd: this.searchForm.get('notifyDtEnd')?.value ?? endOfDay((new Date()))
    }

    this.notificationService.exportNotifyInformation(params, this.laasSetting.language)
    .subscribe({
      next: result => {
        console.log('result',result)
        if (result == null || result.size === 0) {
          this.toastService.info(this.translateService.instant('Common.Toast.NoDataFound'));
          this.loader.hide();
          return;
        }
        const file = result;
        const url = URL.createObjectURL(file);
        const a: any = document.createElement('a');
        file.name = '鈴鐺通知.xlsx';
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: err => {
        this.toastErrorMessage(err.error);
      }
    });
  }
}
